import { ref, onMounted, onUnmounted, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'

/**
 * Detecta qual seção está visível no scroll e atualiza o hash da URL
 * para manter o menu ativo sincronizado
 */
export function useActiveSection() {
  const router = useRouter()
  const route = useRoute()
  const activeSection = ref(null)
  let scrollTimeout = null
  let scrollListenerActive = false

  const sections = [
    { id: 'horarios', offset: 0 },
    { id: 'comoChegar', offset: 0 },
    { id: 'corpo', offset: 0 },
    { id: 'contato', offset: 0 }
  ]

  const updateActiveSection = () => {
    // SEGURANÇA: Verifica novamente se ainda está na home
    if (route.path !== '/') {
      console.log('🚫 [ActiveSection] Não está na home, ignorando:', route.path)
      return
    }

    // Cancela timeout anterior se existir
    if (scrollTimeout) {
      clearTimeout(scrollTimeout)
    }

    // Debounce para não atualizar muito rápido
    scrollTimeout = setTimeout(() => {
      // Última verificação antes de modificar o router
      if (route.path !== '/') {
        console.log('🚫 [ActiveSection] Rota mudou durante timeout, cancelando')
        return
      }

      const scrollPosition = window.scrollY + 150 // Offset do header

      // Se estiver no topo da página (antes de qualquer seção)
      if (scrollPosition < 300) {
        if (route.hash) {
          // Remove o hash para voltar ao estado "Início"
          router.replace({ path: '/', hash: '' })
        }
        activeSection.value = null
        return
      }

      // Verifica qual seção está visível
      let currentSection = null
      
      for (const section of sections) {
        const element = document.getElementById(section.id)
        if (element) {
          const rect = element.getBoundingClientRect()
          const elementTop = rect.top + window.scrollY
          const elementBottom = elementTop + rect.height

          // Se o scroll está dentro da seção
          if (scrollPosition >= elementTop - 200 && scrollPosition < elementBottom) {
            currentSection = section.id
            break
          }
        }
      }

      // Atualiza o hash se mudou de seção
      if (currentSection && currentSection !== activeSection.value) {
        activeSection.value = currentSection
        const newHash = `#${currentSection}`
        if (route.hash !== newHash) {
          router.replace({ path: '/', hash: newHash })
        }
      }
    }, 100)
  }

  // Função para habilitar o listener
  const enableScrollListener = () => {
    if (!scrollListenerActive && route.path === '/') {
      console.log('✅ [ActiveSection] Habilitando scroll listener na home')
      window.addEventListener('scroll', updateActiveSection, { passive: true })
      scrollListenerActive = true
      updateActiveSection()
    }
  }

  // Função para desabilitar o listener
  const disableScrollListener = () => {
    if (scrollListenerActive) {
      console.log('❌ [ActiveSection] Desabilitando scroll listener')
      window.removeEventListener('scroll', updateActiveSection)
      scrollListenerActive = false
      if (scrollTimeout) {
        clearTimeout(scrollTimeout)
      }
    }
  }

  onMounted(() => {
    // Watch para habilitar/desabilitar baseado na rota
    watch(() => route.path, (newPath) => {
      if (newPath === '/') {
        enableScrollListener()
      } else {
        disableScrollListener()
      }
    }, { immediate: true })
  })

  onUnmounted(() => {
    disableScrollListener()
  })

  return {
    activeSection
  }
}
